import React from 'react'
import { Package, RefreshCw, Loader2 } from 'lucide-react'
import { Card } from '../../../../../../components/ui/Card'
import { Badge } from '../../../../../../components/ui/Badge'

/**
 * Two-column layout: OF Summary (left) + Recipe & Components (right).
 * Compact view designed to show reference data without excessive scrolling.
 */
function OrderSummaryAndRecipe({
  selectedOrder,
  recipe,
  consumptions,
  declaredQuantitiesByComponent = {},
  loadingRecipe,
  loadingRecipeFromSap = false,
  onLoadRecipeFromSAP,
  readOnly = false,
}) {
  const sapRecipeBtnBusy = loadingRecipe || loadingRecipeFromSap

  const progress = selectedOrder.QuantityToProduce > 0
    ? Math.round((selectedOrder.QuantityProduced / selectedOrder.QuantityToProduce) * 100)
    : 0

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
      {/* Left column: OF Summary (1/3 width) */}
      <Card className="lg:col-span-1">
        <div className="p-4">
          <h3 className="text-sm font-semibold text-gray-400 mb-3 uppercase flex items-center gap-2">
            <Package className="w-4 h-4 text-blue-400" />
            Resumen OF
          </h3>

          <div className="space-y-3">
            {/* Product info */}
            <div>
              <p className="text-xs text-gray-500">Producto</p>
              <p className="text-sm text-white font-medium truncate">{selectedOrder.ProductName || '-'}</p>
            </div>
            {selectedOrder.OrderNType && (
              <div>
                <p className="text-xs text-gray-500">Tipo</p>
                <p className="text-sm text-gray-300">{selectedOrder.OrderNType}</p>
              </div>
            )}

            {/* Quantities grid */}
            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-gray-700">
              <div className="text-center">
                <p className="text-xs text-gray-500">A Producir</p>
                <p className="text-lg font-bold text-blue-400">{selectedOrder.QuantityToProduce || '0'}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Producido</p>
                <p className="text-lg font-bold text-green-400">{selectedOrder.QuantityProduced || '0'}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">Pendiente</p>
                <p className="text-lg font-bold text-orange-400">{selectedOrder.QuantityRemainedToProduce || '0'}</p>
              </div>
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <p className="text-xs text-gray-400">Progreso</p>
                <p className="text-sm font-bold text-white">{progress}%</p>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-600 to-green-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Right column: Recipe & Components (2/3 width) */}
      <Card className="lg:col-span-2">
        <div className="p-4">
          {loadingRecipe ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin mr-3" />
              <p className="text-gray-400">Cargando receta...</p>
            </div>
          ) : recipe && recipe.Components && recipe.Components.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-400 uppercase">Receta y Componentes</h3>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 font-mono">{recipe.SapCode || selectedOrder.OrderNumber}</span>
                  <Badge variant="info" className="text-xs">
                    {recipe.Components.length} comp.
                  </Badge>
                </div>
              </div>
              <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-800">
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Codigo SAP</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Descripcion</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Dosificador(es)</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-400">Cant. Requerida</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-400">Ud.</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-400">Cant. Declarada</th>
                      <th className="text-right py-2 px-3 text-xs font-semibold text-gray-400">Cant. Retirada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipe.Components.map((component, index) => {
                      const componentCode = (component.SapCode || '').trim()
                      const declaredQty = declaredQuantitiesByComponent[componentCode?.toUpperCase()] ?? 0

                      return (
                        <tr key={index} className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors">
                          <td className="py-2 px-3 text-gray-300 font-mono text-xs">{component.SapCode || '-'}</td>
                          <td className="py-2 px-3 text-gray-300 text-xs truncate max-w-[150px]" title={component.Description}>{component.Description || '-'}</td>
                          <td className="py-2 px-3">
                            {component.DosingUnits && component.DosingUnits.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {component.DosingUnits.map((du, idx) => (
                                  <Badge key={idx} variant="secondary" className="text-xs py-0">
                                    {du}
                                  </Badge>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-500 text-xs">-</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right text-white font-medium text-xs">{component.RequiredQuantity || '0'}</td>
                          <td className="py-2 px-3 text-gray-400 text-xs">{component.MeasurementUnitRQ || '-'}</td>
                          <td className="py-2 px-3 text-right text-white font-medium text-xs">
                            {declaredQty > 0 ? declaredQty.toFixed(3) : '0'}
                          </td>
                          <td className="py-2 px-3 text-right text-white font-medium text-xs">{component.WithDrawnQuantity || '0'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : recipe && (!recipe.Components || recipe.Components.length === 0) ? (
            <div className="text-center py-6">
              <p className="text-sm text-gray-400 mb-2">
                Receta <span className="font-mono text-white">{recipe.SapCode || selectedOrder.OrderNumber}</span> sin componentes
              </p>
              {!readOnly && (
              <button
                type="button"
                onClick={() => onLoadRecipeFromSAP(true)}
                disabled={sapRecipeBtnBusy}
                className="flex items-center gap-2 px-4 py-2 mx-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
              >
                {loadingRecipeFromSap ? (
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                ) : (
                  <RefreshCw className="w-4 h-4 shrink-0" />
                )}
                <span>{loadingRecipeFromSap ? 'Cargando desde SAP…' : 'Cargar desde SAP'}</span>
              </button>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-sm text-gray-400 mb-2">No hay receta registrada</p>
              {!readOnly && (
              <button
                type="button"
                onClick={() => onLoadRecipeFromSAP(true)}
                disabled={sapRecipeBtnBusy}
                className="flex items-center gap-2 px-4 py-2 mx-auto bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
              >
                {sapRecipeBtnBusy ? (
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                ) : (
                  <RefreshCw className="w-4 h-4 shrink-0" />
                )}
                <span>{loadingRecipeFromSap ? 'Cargando desde SAP…' : 'Cargar desde SAP'}</span>
              </button>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}

export default React.memo(OrderSummaryAndRecipe)
